<template>
  <BasePaneCard
    heading="Perfil de finalização da base"
    headingTooltip="Analise a qualidade da sua base por finalização<br>das conversas (conversão e recusa)."
    :isLoading="isLoading"
  >
    <div class="conversation-average">
      <div v-if="!isNotApplicableStatusFilter">
        <div
          v-for="status in averageByStatus"
          :key="status.id"
          class="conversation-average__status"
        >
          <div class="conversation-average__status__label">
            {{ status.label }}
          </div>

          <div class="conversation-average__status__total">
            {{ status.average | number }}
          </div>
        </div>
      </div>

      <BaseEmptyState v-else>
        <template v-if="pageFilter?.status?.length">
          <SvgIconBalloonCross/>

          <h3>
            As conversas filtradas por este<br>
            status não possuem média de<br>
            conversão ou recusa
          </h3>
        </template>

        <template v-else>
          <SvgIconRobot/>

          <h3>
            Ops! Algo deu errado...
          </h3>

          <p>
            Por favor tente novamente.
          </p>
        </template>
      </BaseEmptyState>
    </div>
  </BasePaneCard>
</template>

<script>
import { dateFilter, numberFilter } from '@/utils/filters'
import { pageFilterExceptionMixin } from '@/utils/mixins'
import BasePaneCard from '@/components/base/BasePaneCard'
import BaseEmptyState from '@/components/base/BaseEmptyState'
import { mapState, mapGetters, mapMutations } from 'vuex'
import axios from 'axios'
import dayjs from 'dayjs'

export default {
  name: 'WidgetConversationAverage',

  components: {
    BasePaneCard,
    BaseEmptyState
  },

  filters: {
    date: dateFilter,
    number: numberFilter
  },

  mixins: [
    pageFilterExceptionMixin
  ],

  data () {
    return {
      isLoading: false,
      dataset: null
    }
  },

  computed: {
    ...mapState('base', [
      'mainApi',
      'pageFilter'
    ]),

    ...mapGetters('auth', [
      'userId',
      'platformId'
    ]),

    averageTotal () {
      return this.dataset?.average_total || 0
    },

    averageByStatus () {
      if (!this.dataset) return []

      return Object.entries(this.statusLabelEnum)
        .map(([id, label]) => {
          return {
            id,
            label,
            average: this.dataset?.average_by_status[id] || 0
          }
        })
    },

    statusLabelEnum () {
      if (
        !this.pageFilter?.status?.length ||
        (this.pageFilter.status.includes(3) && this.pageFilter.status.includes(16))
      ) {
        return {
          3: 'Média de mensagens para conversões',
          16: 'Média de mensagens para recusas'
        }
      }

      if (this.pageFilter.status.includes(3)) {
        return {
          3: 'Média de mensagens para conversões'
        }
      }

      if (this.pageFilter.status.includes(16)) {
        return {
          16: 'Média de mensagens para recusas'
        }
      }
    },

    isNotApplicableStatusFilter () {
      return this.pageFilter?.status?.length && !(
        this.pageFilter?.status?.includes(3) ||
        this.pageFilter?.status?.includes(16)
      )
    }
  },

  watch: {
    pageFilter () {
      this.fetchData()
    }
  },

  mounted () {
    this.fetchData()
  },

  methods: {
    ...mapMutations('smartSummary', {
      setConversationAverages: 'SET_CONVERSATION_AVERAGES'
    }),

    getFetchDataParams () {
      let params = ''
      let [dateStart, dateEnd] = this.pageFilter.dateRange
      const { action, csat, feeling } = this.pageFilter
      const statuses = Object.keys(this.statusLabelEnum)

      if (!dateStart || !dateEnd) {
        dateStart = dayjs()
        dateEnd = dateStart.add(1, 'day')
      }

      params += `?firstDate=${dateFilter(dateStart, 'base')}&lastDate=${dateFilter(dateEnd, 'base')}`
      params += `&statuses=${statuses.join()}`

      if (action.length) {
        params += `&actions=${action.join()}`
      }

      if (csat.length) {
        params += `&csat=${csat.join()}`
      }

      if (feeling.length) {
        params += `&sentiments=${feeling.join()}`
      }

      return params
    },

    async fetchData () {
      this.isLoading = true

      try {
        const params = this.getFetchDataParams()
        const url = `${this.mainApi}lead/message/chat-average${params}`
        const response = await axios.get(url)

        this.dataset = response.data

        this.setConversationAverages(this.averageByStatus)
      } catch (error) {
        this.dataset = { error }

        throw error
      } finally {
        this.isLoading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.conversation-average {
  margin-top: 10px;

  &__total {
    padding-bottom: 10px;

    /deep/ {
      .label {
        font-size: 24px;
      }
    }
  }

  &__status {
    display: flex;
    justify-content: space-between;
    padding: 15px;
    margin-top: 10px;
    border-radius: 8px;
    background: nth($colors-custom, 9);
    line-height: 130%;

    &__label {
      font: {
        weight: 700;
        size: 14px;
      }
    }

    &__total {
      font: {
        weight: 400;
        size: 20px;
      }
    }
  }
}
</style>
